import { colors } from '@globals/themes/defaultTheme'
import { CelebrationTypeEnum, MemberEvents } from '@kudos/http-client'
import { formatProfileEventDate } from '@tenant/me/memberProfileInfoUtils'
import { t } from '@transifex/native'
import { format } from 'date-fns'
import { isWithinRange, MemberCelebration } from './memberEvents'

export type CelebrationBannerType =
  | CelebrationTypeEnum.BIRTHDAY
  | CelebrationTypeEnum.WELCOME
  | CelebrationTypeEnum.ANNIVERSARY

export type CelebrationBanner = {
  type: CelebrationBannerType
  milestone: MemberCelebration
  label: string
  bgColor: string
}

// Intentionally distinct from `celebrationBgColor` in celebrationDisplayStyleUtils:
// the welcome banner uses `blueExtraLight` here by design.
const bannerBgColor: Record<CelebrationBannerType, string> = {
  [CelebrationTypeEnum.WELCOME]: colors.blueExtraLight,
  [CelebrationTypeEnum.BIRTHDAY]: colors.goldUltraLight,
  [CelebrationTypeEnum.ANNIVERSARY]: colors.irisUltraLight,
}

const BANNER_MILESTONE: Record<CelebrationBannerType, MemberCelebration> = {
  [CelebrationTypeEnum.WELCOME]: MemberCelebration.WELCOME,
  [CelebrationTypeEnum.BIRTHDAY]: MemberCelebration.BIRTHDAY,
  [CelebrationTypeEnum.ANNIVERSARY]: MemberCelebration.ANNIVERSARY,
}

const BANNER_LABEL_BUILDERS: Record<
  CelebrationBannerType,
  (firstName: string, date: string, isPast: boolean) => string
> = {
  // The welcome window opens two weeks before the start date and stays open for
  // 90 days after it, so the banner has to switch tense once the day has passed.
  [CelebrationTypeEnum.WELCOME]: (firstName, date, isPast) =>
    isPast
      ? t("{firstName}'s start date was {date}!", { firstName, date })
      : t("{firstName}'s start date is {date}!", { firstName, date }),
  [CelebrationTypeEnum.BIRTHDAY]: (firstName, date) =>
    t("{firstName}'s birthday is {date}!", { firstName, date }),
  [CelebrationTypeEnum.ANNIVERSARY]: (firstName, date) =>
    t("{firstName}'s anniversary is {date}!", { firstName, date }),
}

const buildBanner = (
  type: CelebrationBannerType,
  firstName: string,
  isoDate: string,
  todayKey: string,
): CelebrationBanner => ({
  type,
  milestone: BANNER_MILESTONE[type],
  label: BANNER_LABEL_BUILDERS[type](
    firstName,
    formatProfileEventDate(isoDate),
    isoDate < todayKey,
  ),
  bgColor: bannerBgColor[type],
})

/**
 * Returns the member's active celebration banners in fixed order: birthday →
 * welcome → anniversary. Each banner is included only when the member is
 * within the matching highlight range and the corresponding *Public flag is
 * true, so a celebration they keep private is never surfaced.
 */
export const getActiveCelebrationBanners = (
  events: MemberEvents | null | undefined,
  firstName: string,
  today: Date = new Date(),
): CelebrationBanner[] => {
  if (!events) {
    return []
  }

  const todayKey = format(today, 'yyyy-MM-dd')
  const banners: CelebrationBanner[] = []

  if (isWithinRange(todayKey, events.birthdayRange) && events.birthdayPublic && events.birthday) {
    banners.push(buildBanner(CelebrationTypeEnum.BIRTHDAY, firstName, events.birthday, todayKey))
  }

  const welcomeWindowActive = !!events.welcome && isWithinRange(todayKey, events.welcomeRange)
  if (welcomeWindowActive && events.welcomePublic && events.welcome) {
    banners.push(buildBanner(CelebrationTypeEnum.WELCOME, firstName, events.welcome, todayKey))
  }

  if (
    !welcomeWindowActive &&
    isWithinRange(todayKey, events.anniversaryRange) &&
    events.anniversaryPublic &&
    events.anniversary
  ) {
    banners.push(
      buildBanner(CelebrationTypeEnum.ANNIVERSARY, firstName, events.anniversary, todayKey),
    )
  }

  return banners
}
